import { Connection, Edge, Node, ReactFlowInstance, addEdge, UpdateNodeInternals } from "reactflow";
import { IBannerContext, BannerStatus } from "@plex/react-components";
import {
  getSourceSystem,
  IControlFlowParam,
  IDataParam,
  IDataTypeOptions,
  INodeProperty,
  INodeTypeDefinition,
  ISchemaPickerOptions,
  NodeType,
  ReferencePropertyType,
  sourceSystemIncludesDataParam
} from "../NodeTypes/Base";
import { camelCaseToSpacesRegEx } from "../NodePropertiesForm/FormConstants";
import {
  DocumentSchemaType,
  getIdentifierName,
  getIncludedFieldName,
  IDocumentListSchema,
  IDocumentObjectSchema,
  isDynamicObjectSchema,
  propertyIsIdentifier,
  propertyIsCompositeIdentifier,
  getSchemaObjectName
} from "../FlowDocument/FlowDocumentModel";
import {
  forceNodeUpdate,
  getAvailableConfigDataTypes,
  setSchemaIdForLoopEndVariables,
  updateNodeDataProperties,
  updateNodeTimestamp
} from "../Util/NodeUtil";
import {
  IDesignerSchema,
  addListSchemaIfNotExists,
  getDataTypeFromSchema,
  getDataTypeFromSchemaInstance,
  getEmptySchema,
  getEmptySchemaId,
  getSchema,
  isEmptySchema,
  noReferenceSchemaId,
  setObjectSchemas
} from "../NodeTypes/DataSchemas";
import {
  DataType,
  getDataType,
  implicit,
  implicitSecondary,
  isObjectLike,
  isListType,
  dataTypes
} from "../NodeTypes/TypeDefinitions";
import { AddMenuNodePopulater, IDataTypeConfig } from "../AddNodeMenu/AddMenuNodePopulation";
import { validateOrder } from "../Runtime/RuntimeValidation";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";
import { IConnectedSchemaTagsOptions } from "../NodeTypes/Base";
import { AnchorProperties } from "./AnchorUtil";
import { openConfirmDialog } from "./DialogUtil";
import { IFlowOrderError } from "../Runtime/RuntimeExecution";
import { upperSnakeToCamelCase } from "../FlowDocument/DocumentProcessor";
import { setLinkedSchemaPropertySelectionToDefault } from "../NodePropertiesForm/SchemaPickerField";

export interface IHandleInfo {
  handleId: string;
  name: string;
  readableName: string;
  type: string;
  node: Node<any>;
}

export interface IEdgeInfo {
  targetHandle: IHandleInfo;
  sourceHandle: IHandleInfo;
}

export class EdgeDetail {
  private readonly nodeId: string;
  private readonly reactFlowInstance: ReactFlowInstance;
  private readonly edges?: Edge[];
  private readonly nodes?: Node[];

  constructor(nodeId: string, reactFlowInstance: ReactFlowInstance, edges?: Edge[], nodes?: Node[]) {
    this.nodeId = nodeId;
    this.reactFlowInstance = reactFlowInstance;
    this.edges = edges;
    this.nodes = nodes;
  }

  getInputTargetLabel(inputName: string): string {
    let inputTargetHandle = this.getInput(inputName)?.targetHandle;
    let inputLabel = inputTargetHandle?.readableName ?? "";

    if (inputTargetHandle) {
      let outputName = inputTargetHandle?.name;
      let outputLabel = inputTargetHandle.node.data.nodeProperties.outputs[outputName].label;

      if (outputLabel) {
        inputLabel = outputLabel;
      }
    }

    if (inputTargetHandle?.node.type === "constant") {
      switch (inputTargetHandle.type) {
        case "string":
          inputLabel = `"${inputTargetHandle.node.data.nodeProperties.literal}"`;
          break;
        default:
          inputLabel = `${inputTargetHandle.node.data.nodeProperties.literal}`;
          break;
      }
    }

    return inputTargetHandle?.type === "string" ? (inputLabel === "" ? '""' : inputLabel) : inputLabel;
  }

  getInput(inputName: string): IEdgeInfo | null {
    let inputEdge = (this.edges ?? this.reactFlowInstance.getEdges()).filter(
      (e) => e.source === this.nodeId && this.getHandle(e.source, e.sourceHandle).name === inputName
    )[0];

    if (!inputEdge) {
      return null;
    }

    return {
      targetHandle: this.getHandle(inputEdge.target, inputEdge.targetHandle),
      sourceHandle: this.getHandle(inputEdge.source, inputEdge.sourceHandle)
    };
  }

  getOutput(outputName: string): IEdgeInfo | null {
    let outputEdge = (this.edges ?? this.reactFlowInstance.getEdges()).filter(
      (e) => e.target === this.nodeId && this.getHandle(e.target, e.targetHandle).name === outputName
    )[0];

    if (!outputEdge) {
      return null;
    }

    return {
      targetHandle: this.getHandle(outputEdge.target, outputEdge.targetHandle),
      sourceHandle: this.getHandle(outputEdge.source, outputEdge.sourceHandle)
    };
  }

  private getHandle(nodeId: string, handleId: string | null | undefined): IHandleInfo {
    const parsed = parseDataHandleId(handleId ?? "");
    let node = this.nodes ? this.nodes.find((n: Node) => n.id === nodeId) : this.reactFlowInstance.getNode(nodeId);
    return {
      handleId: handleId ?? "",
      name: parsed.name,
      readableName: getReadableName(parsed.name),
      type: parsed.type,
      node: node!
    };
  }
}

let controlFlowErrors: IFlowOrderError[] = [];

export const hasNodeConnectionErrors = (fromNodeId: String, toNodeId: String): boolean => {
  return controlFlowErrors.some((error) => error.fromNode.data.id === fromNodeId && error.toNode.data.id === toNodeId);
};

const getReadableName = (name: string) => {
  if (name === null || name === undefined) {
    return "";
  }
  let readableName = name.replaceAll("_", " ");
  readableName = readableName.charAt(0).toUpperCase() + readableName.slice(1);
  readableName = readableName.replace(camelCaseToSpacesRegEx, "$1$4 $2$3$5");
  return readableName;
};

export const updateEdgeValidationStyling = (currentEdges?: Edge[]) => {
  const reactFlowInstance = AddMenuNodePopulater._reactFlowInstance;
  const updateNodeInternals = AddMenuNodePopulater._reactFlowUpdateNodeInternals;

  setTimeout(() => {
    controlFlowErrors = validateOrder({
      nodes: reactFlowInstance.getNodes(),
      edges: currentEdges ?? reactFlowInstance.getEdges()
    });
    let errorEdges = (currentEdges ?? reactFlowInstance.getEdges()).filter((edge) =>
      controlFlowErrors.some(
        (error) =>
          (edge.source === error.fromNode.id && edge.target === error.toNode.id) ||
          (edge.source === error.toNode.id && edge.target === error.fromNode.id)
      )
    );

    // Reset styling on edges without errors
    let resetStyleEdges = (currentEdges ?? reactFlowInstance.getEdges()).filter(
      (edge) => !errorEdges.some((error) => error.id === edge.id)
    );
    resetStyleEdges.forEach((edge) => {
      edge.className = edge.className?.replaceAll("edge-flow-order-error", "");
    });

    if (controlFlowErrors.length > 0) {
      errorEdges.forEach((edge) => {
        edge.className += " edge-flow-order-error";
      });
    }

    reactFlowInstance.setEdges((edges) =>
      (currentEdges ?? edges)
        .filter(
          (e) => !errorEdges.some((error) => error.id === e.id) && !resetStyleEdges.some((error) => error.id === e.id)
        )
        .concat(errorEdges)
        .concat(resetStyleEdges)
    );

    setSchemaIdForLoopEndVariables(reactFlowInstance, updateNodeInternals, currentEdges);
  }, 50);
};

export const handleEdgeConnection = (params: Connection | Edge, loadingDocument?: boolean, nodes?: Node<any>[]) => {
  return handleEdgeConnections([params], loadingDocument, nodes);
};

export type DataConnectionConfig = {
  target?: IDataTypeConfig;
  source?: IDataTypeConfig;
};

export interface IConnectionValidationResult {
  valid: boolean;
  onlyValidFromImplicitConversion: boolean;
  onlyValidFromSecondaryImplicitConversion: boolean;
  requiredDataConfig?: DataConnectionConfig;
  referenceMismatches: { referenceNameA: string; referenceNameB: string }[];
  multipleConnectionsToSingleConnectionOutput: boolean;
}

export interface INodeRemapProperty {
  name: string;
  label: string;
  edges: (Edge | Connection)[];
}

export interface INodeRemap {
  nodeId: string;
  nodeLabel: string;
  remapProperties: INodeRemapProperty[];
}

export interface IConnectionRemap {
  nodes: INodeRemap[];
}

export const canDeleteEdge = (edge: Edge, reactFlowInstance: ReactFlowInstance, bannerContext: IBannerContext) => {
  const edges = reactFlowInstance.getEdges();
  const targetNode = reactFlowInstance.getNode(edge.target);
  const sourceNode = reactFlowInstance.getNode(edge.source);
  if (
    targetNode &&
    sourceNode &&
    (sourceNode.data.nodeProperties.schemaSourceNodeId || targetNode.data.nodeProperties.schemaLinkedNodes)
  ) {
    const schemaSourceNode = targetNode.data.nodeProperties.schemaLinkedNodes
      ? targetNode
      : reactFlowInstance.getNode(sourceNode.data.nodeProperties.schemaSourceNodeId);
    if (schemaSourceNode) {
      const objectOutputPropertyNodes: Node<any>[] = [];
      traverseSchemaLinkedOutputs({
        node: sourceNode,
        nodes: reactFlowInstance.getNodes(),
        edges: edges,
        onNodeVisit: (node) => {
          const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node!.type!)!;
          if (nodeDefinition.outputsSchemaProperties) {
            objectOutputPropertyNodes.push(node);
          }
        }
      });
      const hasPropertyNodesWithConnectedProperties = objectOutputPropertyNodes.some((n: Node<any>) =>
        edges.some((e: Edge) => e.target === n.id)
      );
      if (hasPropertyNodesWithConnectedProperties) {
        bannerContext.addMessage("Action aborted due to existing dependent connections.", BannerStatus.warning);
        return false;
      }
    }
  }

  return true;
};

export const getSchemaLinkedUnmappable = (
  edge: Edge | Connection,
  schemaMappingTo: IDesignerSchema,
  reactFlowInstance: ReactFlowInstance
) => {
  const unmappableOutputs: IConnectionRemap = { nodes: [] };
  const edges = reactFlowInstance.getEdges();
  const targetNode = reactFlowInstance.getNode(edge.target!);
  const sourceNode = reactFlowInstance.getNode(edge.source!);
  if (
    targetNode &&
    sourceNode &&
    (sourceNode.data.nodeProperties.schemaSourceNodeId || targetNode.data.nodeProperties.schemaLinkedNodes)
  ) {
    const schemaSourceNode = targetNode.data.nodeProperties.schemaLinkedNodes
      ? targetNode
      : reactFlowInstance.getNode(sourceNode.data.nodeProperties.schemaSourceNodeId);
    const inputName = getNameFromDataHandleId(edge.sourceHandle!);
    const outputName = getNameFromDataHandleId(edge.targetHandle!);
    if (schemaSourceNode && targetNode.data.nodeProperties.outputs[outputName]) {
      const sourceNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(sourceNode.type!);
      const sourceNodeIsSchemaSource = sourceNodeDefinition?.nodeConfigProperties?.some(
        (nodeConfigProperty: INodeProperty) =>
          nodeConfigProperty.schemaSourceSystemType !== undefined &&
          sourceSystemIncludesDataParam(nodeConfigProperty.schemaSourceSystemType, inputName)
      );

      // Do not do a traversal check on a schema change for nodes that are a schema source,
      //   since they will define their own schema and not link through this edge.
      if (!sourceNodeIsSchemaSource) {
        traverseSchemaLinkedOutputs({
          traverseAlongsideSchema: schemaMappingTo,
          node: sourceNode,
          nodes: reactFlowInstance.getNodes(),
          edges: edges,
          fromEdge: edge,
          onOutputVisit(input, output, node, edges, traversedAlongsideSchema) {
            const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
            if (nodeDefinition?.outputsSchemaProperties) {
              // If there are output edges, but no schema to map to.
              if (edges.length > 0 && traversedAlongsideSchema === undefined) {
                let nodeRemapIndex = unmappableOutputs.nodes.findIndex(
                  (nodeRemap: INodeRemap) => nodeRemap.nodeId === node.id
                );
                if (nodeRemapIndex === -1) {
                  unmappableOutputs.nodes.push({
                    nodeId: node.id,
                    nodeLabel: node.data.nodeProperties.name
                      ? node.data.nodeProperties.name
                      : node.data.nodeProperties.label,
                    remapProperties: []
                  });
                  nodeRemapIndex = unmappableOutputs.nodes.length - 1;
                }
                unmappableOutputs.nodes[nodeRemapIndex]!.remapProperties.push({
                  name: output.name,
                  label: output.label ?? getReadableName(output.name),
                  edges: edges
                });
              }
            }
          }
        });
      }
    }
  }

  return unmappableOutputs;
};

const getUnmappableEdges = (remap: IConnectionRemap, reactFlowInstance: ReactFlowInstance) => {
  const unmappableEdges: (Edge | Connection)[] = [];
  remap.nodes.forEach((nodeRemap: INodeRemap) =>
    nodeRemap.remapProperties.forEach((property: INodeRemapProperty) =>
      property.edges.forEach((unmappableEdge: Edge | Connection) => unmappableEdges.push(unmappableEdge))
    )
  );
  return unmappableEdges;
};

const removeUnmappableEdges = (
  unmappableEdges: (Edge | Connection)[],
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals
) => {
  let newEdges: Edge[] = [];
  reactFlowInstance.setEdges(
    (edges: Edge[]) =>
      (newEdges = edges.filter(
        (e: Edge) =>
          !unmappableEdges.some(
            (unmappableEdge: Edge | Connection) =>
              unmappableEdge.targetHandle &&
              unmappableEdge.sourceHandle &&
              unmappableEdge.targetHandle === e.targetHandle &&
              unmappableEdge.sourceHandle === e.sourceHandle
          )
      ))
  );
  const affectedNodes = reactFlowInstance
    .getNodes()
    .filter((n: Node<any>) => unmappableEdges.some((e: Edge) => e.source === n.id || e.target === n.id));
  affectedNodes.forEach((n: Node<any>) => {
    forceNodeUpdate(n, updateNodeInternals, reactFlowInstance);
  });

  return newEdges;
};

export interface INewEdgeSchema {
  edge: Edge | Connection;
  newSchema: IDesignerSchema | undefined;
}

const getEmptySchemaFromEdge = (edge: Edge | Connection, reactFlowInstance: ReactFlowInstance) => {
  const edgeDetail = new EdgeDetail(edge.target!, reactFlowInstance);
  const outputName = getNameFromDataHandleId(edge.targetHandle!);
  const newSchema = getEmptySchema(edgeDetail.getOutput(outputName)!.targetHandle.name) as unknown as IDesignerSchema;
  return newSchema;
};

export const checkAndRemoveUnmappable = (
  edgeSchemas: INewEdgeSchema[],
  confirmCallback: (newEdges: Edge[]) => void,
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals
) => {
  const remap: IConnectionRemap = { nodes: [] };
  edgeSchemas.forEach((edgeSchema: INewEdgeSchema) => {
    const newSchema = edgeSchema.newSchema ?? getEmptySchemaFromEdge(edgeSchema.edge, reactFlowInstance);
    const edgeRemap = getSchemaLinkedUnmappable(edgeSchema.edge, newSchema, reactFlowInstance);
    edgeRemap.nodes.forEach((nodeRemap: INodeRemap) => {
      let nodeRemapIndex = remap.nodes.findIndex(
        (findNodeRemap: INodeRemap) => nodeRemap.nodeId === findNodeRemap.nodeId
      );
      if (nodeRemapIndex === -1) {
        remap.nodes.push({ nodeId: nodeRemap.nodeId, nodeLabel: nodeRemap.nodeLabel, remapProperties: [] });
        nodeRemapIndex = remap.nodes.length - 1;
      }
      nodeRemap.remapProperties.forEach((nodeRemapProperty: INodeRemapProperty) => {
        if (
          !remap.nodes[nodeRemapIndex]!.remapProperties.some(
            (p: INodeRemapProperty) => p.name === nodeRemapProperty.name
          )
        )
          remap.nodes[nodeRemapIndex]!.remapProperties.push(nodeRemapProperty);
      });
    });
  });

  const unmappableEdges = getUnmappableEdges(remap, reactFlowInstance);
  if (unmappableEdges.length > 0) {
    openConfirmDialog(remap, () => {
      const newEdges = removeUnmappableEdges(unmappableEdges, reactFlowInstance, updateNodeInternals);
      confirmCallback(newEdges);
    });
  } else {
    confirmCallback(reactFlowInstance.getEdges());
  }
};

export const validateConnection = (
  params: Edge | Connection,
  sourceNodeType: string,
  targetNodeType: string,
  reactFlowInstance: ReactFlowInstance,
  skipLoopValidation: boolean = false
): IConnectionValidationResult => {
  let edgeIsValid = true;
  let multipleConnectionsToSingleConnectionOutput = false;
  let onlyValidFromImplicitConversion = false;
  let onlyValidFromSecondaryImplicitConversion = false;
  let requiredDataConfig: DataConnectionConfig | undefined;
  const referenceMismatches: { referenceNameA: string; referenceNameB: string }[] = [];
  const sourceNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(sourceNodeType);
  const targetNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(targetNodeType);

  if (targetNodeType === NodeType.dateTimeNow || targetNodeType === NodeType.dateRange) {
    if (reactFlowInstance.getEdges().some((e: Edge<any>) => e.target === params.target && e.source !== params.source)) {
      edgeIsValid = false;
      multipleConnectionsToSingleConnectionOutput = true;
    }
  }

  if (params.source === params.target) {
    edgeIsValid = false;
  }

  let sourceHandleIsControlFlow = params.sourceHandle?.indexOf("FlowIn") === 0;
  let targetHandleIsControlFlow =
    params.targetHandle?.indexOf("FlowOut") === 0 || params.targetHandle?.indexOf("FlowError") === 0;
  let sourceHandleIsControlFlowError = params.sourceHandle?.indexOf("FlowError") === 0;
  let targetHandleIsControlFlowError = params.targetHandle?.indexOf("FlowError") === 0;

  if (targetHandleIsControlFlow) {
    let existingFlowOutputEdges = reactFlowInstance
      .getEdges()
      .filter(
        (e) =>
          e.targetHandle === params.targetHandle &&
          e.target === params.target &&
          params.targetHandle?.indexOf("FlowOutHandle") !== -1
      );
    if (existingFlowOutputEdges.length > 0) {
      edgeIsValid = false;
    }

    const controlOutputName = params.targetHandle?.replace("FlowOutHandle", "");
    const controlOutput = targetNodeDefinition?.controlOutputs.find(
      (output: IControlFlowParam) => output.name === controlOutputName
    );

    if (!skipLoopValidation) {
      const targetNode = reactFlowInstance.getNode(params.target!);
      if (targetNode) {
        const insideLoop = controlOutput?.startsLoop || targetNode.data.nodeProperties.insideLoopNodeId;
        if (sourceNodeDefinition?.innerLoop && !insideLoop) {
          edgeIsValid = false;
        }
      }
    }
  }

  if (sourceHandleIsControlFlow) {
    let existingControlInputEdges = reactFlowInstance
      .getEdges()
      .filter(
        (e) =>
          e.sourceHandle === params.sourceHandle &&
          e.source === params.source &&
          params.sourceHandle?.indexOf("FlowInHandle") !== -1
      );
    if (existingControlInputEdges.length > 0) {
      edgeIsValid = false;
    }
  }

  if (!sourceHandleIsControlFlowError && targetHandleIsControlFlow !== sourceHandleIsControlFlow) {
    edgeIsValid = false;
  }

  if (sourceHandleIsControlFlowError && targetHandleIsControlFlowError !== sourceHandleIsControlFlowError) {
    edgeIsValid = false;
  }

  if (params.targetHandle === "FlowOutHandleDone" && sourceNodeType !== "stop") {
    edgeIsValid = false;
  }

  const targetHandleIdParts = params.targetHandle!.split("-");
  if (!sourceHandleIsControlFlow && !targetHandleIsControlFlow) {
    const sourceHandleIdParts = params.sourceHandle!.split("-");
    const sourceHandleDataType = sourceHandleIdParts[sourceHandleIdParts.length - 2]!;
    const sourceHandleName = parseDataHandleId(params.sourceHandle!).name;
    const targetHandleName = parseDataHandleId(params.targetHandle!).name;
    const substringIndex = targetHandleIdParts.includes("error") ? 3 : 2;
    const targetHandleDataType = targetHandleIdParts[targetHandleIdParts.length - substringIndex]!;
    const isObjectReferenceConnection =
      sourceHandleDataType === DataType.OBJECTREFERENCE || targetHandleDataType === DataType.OBJECTREFERENCE;

    if (sourceHandleDataType !== targetHandleDataType) {
      const expandingReferenceObject =
        targetHandleDataType === DataType.OBJECTREFERENCE &&
        sourceHandleDataType === DataType.OBJECT &&
        sourceNodeDefinition?.outputsSchemaProperties;
      if (!expandingReferenceObject) {
        const implicitConversion = implicit(getDataType(targetHandleDataType)!, getDataType(sourceHandleDataType)!);
        if (!implicitConversion) {
          const secondaryImplicitConversion = implicitSecondary(
            getDataType(targetHandleDataType)!,
            getDataType(sourceHandleDataType)!
          );
          if (!secondaryImplicitConversion) {
            if (!isObjectReferenceConnection && params.target && params.source) {
              requiredDataConfig = getRequiredDataConfig(
                params.target,
                targetNodeType,
                targetHandleName,
                targetHandleDataType,
                params.source,
                sourceNodeType,
                sourceHandleName,
                sourceHandleDataType,
                reactFlowInstance
              );
            }

            if (!requiredDataConfig) {
              edgeIsValid = false;
            }
          } else {
            onlyValidFromSecondaryImplicitConversion = true;
          }
        } else {
          onlyValidFromImplicitConversion = true;
        }
      }
    }

    // Object reference connections must use the same reference schema.
    if (sourceHandleDataType === targetHandleDataType && isObjectReferenceConnection) {
      const sourceNode = reactFlowInstance.getNode(params.source!);
      const targetNode = reactFlowInstance.getNode(params.target!);
      const sourceInput = sourceNode?.data.nodeProperties.inputs[sourceHandleName];
      const targetOutput = targetNode?.data.nodeProperties.outputs[targetHandleName];
      let targetOutputSchemaId = targetOutput?.schemaId;
      let sourceInputSchemaId = sourceInput?.schemaId;

      // Use defined data output for the node type if node instance doesn't exist.
      if (!targetOutputSchemaId) {
        targetOutputSchemaId = targetNodeDefinition?.dataOutputs.find(
          (output: IDataParam) => output.name === targetHandleName
        )?.schemaId;
      }

      // Use defined data input for the node type if node instance doesn't exist.
      if (!sourceInputSchemaId) {
        sourceInputSchemaId = sourceNodeDefinition?.dataInputs.find(
          (input: IDataParam) => input.name === sourceHandleName
        )?.schemaId;
      }

      // If the node doesn't adapt reference on connection, the schema id must match.
      //   Otherwise, the reference can be connected to a no reference schema id, and change to the other reference.
      if (
        (!sourceNodeDefinition?.adaptToReferenceSchemaOnConnection || sourceInputSchemaId !== noReferenceSchemaId) &&
        (!targetNodeDefinition?.adaptToReferenceSchemaOnConnection || targetOutputSchemaId !== noReferenceSchemaId) &&
        sourceInputSchemaId !== targetOutputSchemaId
      ) {
        edgeIsValid = false;
        const sourceInputSchema = sourceInputSchemaId ? getSchema(sourceInputSchemaId) : undefined;
        const targetOutputSchema = targetOutputSchemaId ? getSchema(targetOutputSchemaId) : undefined;
        const noReferenceLabel = "No Reference";
        const sourceInputSchemaName = sourceInputSchema ? getSchemaObjectName(sourceInputSchema) : noReferenceLabel;
        const targetOutputSchemaName = targetOutputSchema ? getSchemaObjectName(targetOutputSchema) : noReferenceLabel;
        referenceMismatches.push({
          referenceNameA: sourceInputSchemaName !== "" ? sourceInputSchemaName : noReferenceLabel,
          referenceNameB: targetOutputSchemaName !== "" ? targetOutputSchemaName : noReferenceLabel
        });
      }
    }

    let existingInputEdges = reactFlowInstance.getEdges().filter((edge) => {
      let edgeMatch = edge.sourceHandle === params.sourceHandle && edge.source === params.source;
      return edgeMatch;
    });
    if (existingInputEdges.length > 0) {
      edgeIsValid = false;
    }
  }

  return {
    valid: edgeIsValid,
    onlyValidFromImplicitConversion,
    onlyValidFromSecondaryImplicitConversion,
    requiredDataConfig,
    referenceMismatches,
    multipleConnectionsToSingleConnectionOutput
  };
};

const getRequiredDataConfig = (
  targetNodeId: string,
  targetNodeType: string,
  targetHandleName: string,
  targetHandleDataType: string,
  sourceNodeId: string,
  sourceNodeType: string,
  sourceHandleName: string,
  sourceHandleDataType: string,
  reactFlowInstance: ReactFlowInstance
) => {
  const targetHasDataConnections = reactFlowInstance
    .getEdges()
    .some(
      (e: Edge) =>
        e.targetHandle?.indexOf("FlowOutHandle") === -1 &&
        e.sourceHandle?.indexOf("FlowInHandle") === -1 &&
        (e.target === targetNodeId || e.source === targetNodeId)
    );
  const sourceHasDataConnections = reactFlowInstance
    .getEdges()
    .some(
      (e: Edge) =>
        e.targetHandle?.indexOf("FlowOutHandle") === -1 &&
        e.sourceHandle?.indexOf("FlowInHandle") === -1 &&
        (e.target === sourceNodeId || e.source === sourceNodeId)
    );

  const sourceHandleTypes = getAvailableConfigDataTypes(sourceNodeType, sourceHandleName);
  const targetHandleTypes = getAvailableConfigDataTypes(targetNodeType, targetHandleName);

  let matchingConfigs: DataConnectionConfig[] = [];
  let implicitConfigs: DataConnectionConfig[] = [];
  let secondaryImplicitConfigs: DataConnectionConfig[] = [];
  const sourceHasListOptions = sourceHandleTypes.some((config: IDataTypeConfig) => isListType(config.dataType));
  const sourceHandleIsList = isListType(sourceHandleDataType as DataType);
  const sourceConvertsToList = sourceHandleIsList && !sourceHasListOptions;
  const targetHasListOptions = targetHandleTypes.some((config: IDataTypeConfig) => isListType(config.dataType));
  const targetHandleIsList = isListType(targetHandleDataType as DataType);
  const targetConvertsToList = targetHandleIsList && !targetHasListOptions;
  sourceHandleTypes.forEach((sourceHandleType: IDataTypeConfig) => {
    const convertedSourceHandleDataType = `${sourceHandleType.dataType}${sourceConvertsToList ? "List" : ""}`;
    targetHandleTypes.forEach((targetHandleType: IDataTypeConfig) => {
      const convertedTargetHandleDataType = `${targetHandleType.dataType}${targetConvertsToList ? "List" : ""}`;
      const config = {
        target: targetHandleDataType === convertedTargetHandleDataType ? undefined : targetHandleType,
        source: sourceHandleDataType === convertedSourceHandleDataType ? undefined : sourceHandleType
      };
      const configIsPossible =
        (!config.target || targetHandleType.configProperties) && (!config.source || sourceHandleType.configProperties);
      const configBreaksConnections =
        (config.target && targetHasDataConnections) || (config.source && sourceHasDataConnections);

      if (configIsPossible && !configBreaksConnections) {
        if (convertedSourceHandleDataType === convertedTargetHandleDataType) {
          matchingConfigs.push(config);
        }
        if (implicit(getDataType(convertedTargetHandleDataType)!, getDataType(convertedSourceHandleDataType)!)) {
          implicitConfigs.push(config);
        }
        if (
          implicitSecondary(getDataType(convertedTargetHandleDataType)!, getDataType(convertedSourceHandleDataType)!)
        ) {
          secondaryImplicitConfigs.push(config);
        }
      }
    });
  });

  // Sort configs based on how much the config needs to change, to prefer config combinations involving fewer changes.
  const sortConfigs = (configs: DataConnectionConfig[]) =>
    configs.sort(
      (a: DataConnectionConfig, b: DataConnectionConfig) =>
        (a.source ? 1 : 0) + (a.target ? 1 : 0) - ((b.source ? 1 : 0) + (b.target ? 1 : 0))
    );

  matchingConfigs = sortConfigs(matchingConfigs);

  let requiredDataConfig: DataConnectionConfig | undefined;
  if (matchingConfigs.length > 0) {
    requiredDataConfig = matchingConfigs[0];
  } else {
    implicitConfigs = sortConfigs(implicitConfigs);
    if (implicitConfigs.length > 0) {
      requiredDataConfig = implicitConfigs[0];
    } else {
      secondaryImplicitConfigs = sortConfigs(secondaryImplicitConfigs);
      if (secondaryImplicitConfigs.length > 0) {
        requiredDataConfig = secondaryImplicitConfigs[0];
      }
    }
  }

  return requiredDataConfig;
};

const updateConnectedSchemaTags = (node: Node<any>, nodes: Node<any>[], edges: (Edge | Connection)[]) => {
  const nodeTypeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node!.type!);
  if (!nodeTypeDefinition?.nodeConfigProperties) {
    return;
  }

  nodeTypeDefinition.nodeConfigProperties
    .filter((p: INodeProperty) => p.propertyType === NodeConfigPropertyType.ConnectedSchemaTags)
    .forEach((p: INodeProperty) => {
      const dataParams = (p.options as IConnectedSchemaTagsOptions)?.dataParams ?? [];

      const relevantConnections = edges.filter((e) => {
        const handleName =
          e.source === node!.id
            ? getNameFromDataHandleId(e.sourceHandle!)
            : e.target === node!.id
            ? getNameFromDataHandleId(e.targetHandle!)
            : null;
        return handleName !== null && dataParams.includes(handleName);
      });

      const connectionsByOtherNode = new Map<string, (Edge | Connection)[]>();
      relevantConnections.forEach((connection) => {
        const otherNodeId = connection.source === node!.id ? connection.target : connection.source;
        if (otherNodeId) {
          if (!connectionsByOtherNode.has(otherNodeId)) {
            connectionsByOtherNode.set(otherNodeId, []);
          }
          connectionsByOtherNode.get(otherNodeId)!.push(connection);
        }
      });

      connectionsByOtherNode.forEach((connections, otherNodeId) => {
        const otherNode = nodes.find((n) => n.id === otherNodeId);
        if (otherNode) {
          updateConnectedSchemaTagsFromConnections(node!, otherNode, connections);
        }
      });
    });
};

const updateConnectedSchemaTagsFromConnections = (
  node: Node<any>,
  otherNode: Node<any>,
  connections: (Edge | Connection)[]
) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
  if (!nodeDefinition) {
    return;
  }
  nodeDefinition.nodeConfigProperties
    .filter((p: INodeProperty) => p.propertyType === NodeConfigPropertyType.ConnectedSchemaTags)
    .forEach((p: INodeProperty) => {
      connections.forEach((connection: Edge | Connection) => {
        const dataParams = (p.options as IConnectedSchemaTagsOptions)?.dataParams ?? [];
        const tags: string[] = [];
        let connectedDataParam;
        let otherNodeDataDirection: "input" | "output";
        if (connection.source === node.id) {
          connectedDataParam = getNameFromDataHandleId(connection.sourceHandle!);
          otherNodeDataDirection = "output";
        } else {
          connectedDataParam = getNameFromDataHandleId(connection.targetHandle!);
          otherNodeDataDirection = "input";
        }

        if (connectedDataParam && dataParams.includes(connectedDataParam)) {
          const otherDataParam =
            otherNodeDataDirection === "input"
              ? getNameFromDataHandleId(connection.sourceHandle!)
              : getNameFromDataHandleId(connection.targetHandle!);
          const schemaId =
            otherNodeDataDirection === "input"
              ? otherNode.data.nodeProperties.inputs?.[otherDataParam]?.schemaId
              : otherNode.data.nodeProperties.outputs?.[otherDataParam]?.schemaId;
          const schema = schemaId ? getSchema(schemaId) : undefined;
          const schemaTags: string[] | undefined = (schema as any)?.tags;
          if (schemaTags) {
            schemaTags.forEach((tag: string) => {
              if (!tags.includes(tag)) {
                tags.push(tag);
              }
            });
          }
        }
        node.data.nodeProperties[p.name] = tags;
      });
    });
};

export interface IHandleEdgeConnectionsResult {
  validationResults: IConnectionValidationResult[];
  newEdges: Edge[];
}

// Expose handle so adding and removing edge could be used by Add node
export const handleEdgeConnections = (
  paramsArray: (Connection | Edge)[],
  loadingDocument: boolean = false,
  nodes?: Node<any>[]
): IHandleEdgeConnectionsResult => {
  const reactFlowInstance = AddMenuNodePopulater._reactFlowInstance;
  let currentNodes: Node<any>[];
  if (nodes) {
    currentNodes = nodes;
  } else {
    currentNodes = reactFlowInstance.getNodes();
  }
  const setEdges = AddMenuNodePopulater._reactFlowEdgeSetter;
  const updateNodeInternals = AddMenuNodePopulater._reactFlowUpdateNodeInternals;
  const validationResults: IConnectionValidationResult[] = [];

  const newEdges: (Connection | Edge)[] = [];
  const nodesFromConnections: Node<any>[] = [];

  for (let i = 0; i < paramsArray.length; i++) {
    const params: Edge | Connection = paramsArray[i]!;

    let sourceNode = currentNodes.find((n: Node<any>) => n.id === params.source!)!;
    let targetNode = currentNodes.find((n: Node<any>) => n.id === params.target!)!;
    if (!nodesFromConnections.some((n) => n.id === sourceNode.id)) {
      nodesFromConnections.push(sourceNode);
    }
    if (!nodesFromConnections.some((n) => n.id === targetNode.id)) {
      nodesFromConnections.push(targetNode);
    }
    const sourceHandleName = getNameFromDataHandleId(params.sourceHandle!);
    const targetHandleName = getNameFromDataHandleId(params.targetHandle!);

    const validationResult: IConnectionValidationResult = validateConnection(
      params,
      sourceNode.type!,
      targetNode.type!,
      reactFlowInstance,
      loadingDocument
    );
    validationResults.push(validationResult);
    let edgeIsValid = validationResult.valid;

    const sourceHasDataConnections = reactFlowInstance
      .getEdges()
      .some(
        (e: Edge) =>
          e.sourceHandle?.indexOf("FlowInHandle") !== 0 && (e.source === params.source || e.target == params.source)
      );

    const targetHasDataConnections = reactFlowInstance
      .getEdges()
      .some(
        (e: Edge) =>
          e.sourceHandle?.indexOf("FlowOutHandle") !== 0 && (e.source === params.target || e.target === params.target)
      );

    if (!loadingDocument) {
      // Switch data types to accommodate connection
      if (validationResult.requiredDataConfig) {
        if (validationResult.requiredDataConfig.source) {
          if (sourceHasDataConnections) {
            edgeIsValid = false;
          } else {
            const dataConfigs = validationResult.requiredDataConfig.source.configProperties;
            if (dataConfigs) {
              dataConfigs.forEach((configProperty: { propertyName: string; propertyValue: string }) => {
                sourceNode.data.nodeProperties[configProperty.propertyName] = configProperty.propertyValue;
                updateNodeDataProperties(sourceNode);
                updateNodeInternals(sourceNode.id);
                params.sourceHandle = getInputHandle(sourceHandleName, configProperty.propertyValue).toHandleId();
              });
            }
          }
        }
        if (validationResult.requiredDataConfig.target) {
          if (targetHasDataConnections) {
            edgeIsValid = false;
          } else {
            const dataConfigs = validationResult.requiredDataConfig.target.configProperties;
            if (dataConfigs) {
              dataConfigs.forEach((configProperty: { propertyName: string; propertyValue: string }) => {
                targetNode.data.nodeProperties[configProperty.propertyName] = configProperty.propertyValue;
                updateNodeDataProperties(targetNode);
                updateNodeInternals(targetNode.id);
                params.targetHandle = getInputHandle(targetHandleName, configProperty.propertyValue).toHandleId();
              });
            }
          }
        }
      }
    }

    let sourceHandleIsControlFlow = params.sourceHandle?.indexOf("FlowIn") === 0;
    let targetHandleIsControlFlow =
      params.targetHandle?.indexOf("FlowOut") === 0 || params.targetHandle?.indexOf("FlowError") === 0;
    let sourceHandleIsControlFlowError = params.sourceHandle?.indexOf("FlowError") === 0;
    let targetHandleIsControlFlowError = params.targetHandle?.indexOf("FlowError") === 0;

    let sourceHandleIdParts = params.sourceHandle!.split("-");
    let targetHandleIdParts = params.targetHandle!.split("-");
    if (!sourceHandleIsControlFlow && !targetHandleIsControlFlow) {
      const substringIndex = targetHandleIdParts.includes("error") ? 3 : 2;
      let sourceHandleDataType = sourceHandleIdParts[sourceHandleIdParts.length - substringIndex]!;
      let targetHandleDataType = targetHandleIdParts[targetHandleIdParts.length - substringIndex]!;

      if (
        edgeIsValid &&
        !loadingDocument &&
        (targetHandleDataType === DataType.OBJECT || targetHandleDataType === DataType.OBJECTLIST)
      ) {
        const sourceNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(sourceNode.type!)!;
        if (sourceNodeDefinition.outputsSchemaProperties) {
          Object.keys(sourceNode.data.nodeProperties.outputs).forEach((outputName) => {
            let output = sourceNode.data.nodeProperties.outputs[outputName];
            output.enabled = false;
          });

          if (edgeIsValid) {
            newEdges.push(params);
          }
        }
      }

      if (edgeIsValid) {
        const sourceNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(sourceNode.type!);
        const targetNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(targetNode.type!);
        if (!loadingDocument) {
          const sourceInput = sourceNode.data.nodeProperties.inputs[sourceHandleName];
          const targetOutput = targetNode.data.nodeProperties.outputs[targetHandleName];

          // Set schema id, if connecting a reference to an unreferenced handle.
          if (targetOutput.schemaId !== noReferenceSchemaId || sourceInput.schemaId !== noReferenceSchemaId) {
            const updatePrimitiveType = (node: Node<any>, propertyName: string, fromReferenceSchemaId) => {
              const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
              const identifierDataTypeConfigProperty = nodeDefinition?.nodeConfigProperties.find(
                (p: INodeProperty) =>
                  p.propertyType === NodeConfigPropertyType.DataType &&
                  (p.options as unknown as IDataTypeOptions).referenceProperty === ReferencePropertyType.identifier &&
                  (p.options as unknown as IDataTypeOptions).dataProperties.includes(propertyName)
              );
              if (identifierDataTypeConfigProperty) {
                const referenceSchema = getSchema(fromReferenceSchemaId) as unknown as IDocumentObjectSchema;
                const identifierName = getIdentifierName(referenceSchema)!;
                node.data.nodeProperties[identifierDataTypeConfigProperty.name] = upperSnakeToCamelCase(
                  referenceSchema.properties[identifierName]!.schema
                );
              }
              const includedFieldDataTypeConfigProperty = nodeDefinition?.nodeConfigProperties.find(
                (p: INodeProperty) =>
                  p.propertyType === NodeConfigPropertyType.DataType &&
                  (p.options as unknown as IDataTypeOptions).referenceProperty ===
                    ReferencePropertyType.includedField &&
                  (p.options as unknown as IDataTypeOptions).dataProperties.includes(propertyName)
              );
              if (includedFieldDataTypeConfigProperty) {
                const referenceSchema = getSchema(fromReferenceSchemaId) as unknown as IDocumentObjectSchema;
                const includedFieldName = getIncludedFieldName(referenceSchema)!;
                if (includedFieldName) {
                  node.data.nodeProperties[includedFieldDataTypeConfigProperty.name] = upperSnakeToCamelCase(
                    referenceSchema.properties[includedFieldName]!.schema
                  );
                }
              }

              updateNodeDataProperties(node);
              updateNodeInternals(node.id);
            };
            if (targetHandleDataType === DataType.OBJECTREFERENCE && targetOutput.schemaId === noReferenceSchemaId) {
              targetOutput.schemaId = sourceInput.schemaId;
              updatePrimitiveType(targetNode, targetHandleName, sourceInput.schemaId);
            }
            if (sourceHandleDataType === DataType.OBJECTREFERENCE && sourceInput.schemaId === noReferenceSchemaId) {
              sourceInput.schemaId = targetOutput.schemaId;
              updatePrimitiveType(sourceNode, sourceHandleName, targetOutput.schemaId);
            }
          }

          if (targetNodeDefinition?.inputsSchemaProperties) {
            const targetSchemaId = targetOutput.schemaId;
            const schemaConfigProperty = targetNodeDefinition.nodeConfigProperties.find(
              (configProperty: INodeProperty) =>
                configProperty.name === targetNodeDefinition?.inputsSchemaProperties?.schemaPropertyName
            );

            let identifierProperty: string | undefined = undefined;
            const oldTargetSchemaId =
              targetNode.data.nodeProperties[targetNodeDefinition?.inputsSchemaProperties.schemaPropertyName];
            if (targetSchemaId !== oldTargetSchemaId && !isDynamicObjectSchema(getSchema(targetSchemaId))) {
              targetNode.data.nodeProperties[targetNodeDefinition?.inputsSchemaProperties.schemaPropertyName] =
                targetSchemaId;
              if (schemaConfigProperty) {
                const schemaConfigOptions = schemaConfigProperty.options as ISchemaPickerOptions;
                if (schemaConfigOptions) {
                  const targetSchema = getSchema(targetSchemaId);
                  let targetSchemaName = "";
                  if (targetSchema) {
                    targetSchemaName = getSchemaObjectName(targetSchema);
                  }
                  targetNode.data.nodeProperties[schemaConfigOptions.displayProperty] = targetSchemaName;
                }

                identifierProperty = setLinkedSchemaPropertySelectionToDefault(targetNode, schemaConfigProperty.name);
              }

              if (targetSchemaId) {
                targetNode.data.nodeProperties.inputs = buildNodeInputsFromSchema(
                  targetNode,
                  targetSchemaId,
                  targetNodeDefinition,
                  reactFlowInstance,
                  undefined,
                  undefined,
                  identifierProperty
                );
              }
            }
            forceNodeUpdate(targetNode, updateNodeInternals, reactFlowInstance);
          }

          const changeSchemaFromConnection = (paramNewSchemaId, node, schemaChangeOnConnectionConfigProperty) => {
            const paramNewSchema = getSchema(paramNewSchemaId);
            let newSchemaId = paramNewSchemaId;
            const hasNonListDataProperties = (
              schemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions
            ).dataProperties.some(
              (dataProperty: string) =>
                !isListType(node.data.nodeProperties.inputs[dataProperty]?.type) ||
                !isListType(node.data.nodeProperties.outputs[dataProperty]?.type)
            );
            // Convert new schema to the item schema, only if the new schema is a list and there are non-list data properties to set.
            if (hasNonListDataProperties && paramNewSchema.schemaType === DocumentSchemaType.list) {
              newSchemaId = (paramNewSchema as unknown as IDocumentListSchema).listItemSchema;
            }
            const dataProperties = (schemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions)
              .dataProperties;
            setObjectSchemas(newSchemaId, dataProperties, node.data.nodeProperties.inputs);
            setObjectSchemas(newSchemaId, dataProperties, node.data.nodeProperties.outputs);
            const displayPropertyName = (schemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions)
              .displayProperty;
            const newSchema = getSchema(newSchemaId);
            let schemaName = getSchemaObjectName(newSchema);
            schemaName = schemaName !== "" ? schemaName : newSchemaId;
            node.data.nodeProperties[schemaChangeOnConnectionConfigProperty.name] = newSchemaId;
            node.data.nodeProperties[displayPropertyName] = schemaName;
          };

          const targetSchemaChangeOnConnectionConfigProperty = targetNodeDefinition?.nodeConfigProperties.find(
            (p: INodeProperty) =>
              p.propertyType === NodeConfigPropertyType.Schema &&
              (p.options as unknown as ISchemaPickerOptions).changeSchemaOnConnection &&
              (p.options as unknown as ISchemaPickerOptions).dataProperties.includes(targetHandleName)
          );
          const sourceSchemaChangeOnConnectionConfigProperty = sourceNodeDefinition?.nodeConfigProperties.find(
            (p: INodeProperty) =>
              p.propertyType === NodeConfigPropertyType.Schema &&
              (p.options as unknown as ISchemaPickerOptions).changeSchemaOnConnection &&
              (p.options as unknown as ISchemaPickerOptions).dataProperties.includes(sourceHandleName)
          );

          const bothChangeSchemaOnConnection =
            targetSchemaChangeOnConnectionConfigProperty && sourceSchemaChangeOnConnectionConfigProperty;
          const sourceHasSchemaSet =
            sourceSchemaChangeOnConnectionConfigProperty &&
            sourceNode.data.nodeProperties[sourceSchemaChangeOnConnectionConfigProperty.name];
          const targetHasSchemaSet =
            targetSchemaChangeOnConnectionConfigProperty &&
            targetNode.data.nodeProperties[targetSchemaChangeOnConnectionConfigProperty.name];

          if (
            !targetHasDataConnections &&
            targetSchemaChangeOnConnectionConfigProperty &&
            !isEmptySchema(sourceInput.schemaId) &&
            !isDynamicObjectSchema(getSchema(sourceInput.schemaId)) &&
            (!bothChangeSchemaOnConnection || sourceHasSchemaSet) &&
            !sourceNodeDefinition?.outputsSchemaProperties &&
            sourceNodeDefinition?.nodeConfigProperties.some(
              (p: INodeProperty) =>
                p.schemaSourceSystemType &&
                sourceSystemIncludesDataParam(p.schemaSourceSystemType, getNameFromDataHandleId(sourceHandleName))
            )
          ) {
            addListSchemaIfNotExists(
              sourceInput.schemaId,
              targetNode,
              (targetSchemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions).dataProperties
            );
            changeSchemaFromConnection(sourceInput.schemaId, targetNode, targetSchemaChangeOnConnectionConfigProperty);
            if (targetNodeDefinition?.inputsSchemaProperties) {
              const targetSchemaDataProperties = (
                targetSchemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions
              ).dataProperties;
              targetNode.data.nodeProperties.inputs = buildNodeInputsFromSchema(
                targetNode,
                targetOutput.schemaId,
                targetNodeDefinition,
                reactFlowInstance,
                targetSchemaDataProperties,
                targetOutput.schemaId
              );
            }
          }

          if (
            !sourceHasDataConnections &&
            sourceSchemaChangeOnConnectionConfigProperty &&
            !isEmptySchema(targetOutput.schemaId) &&
            !isDynamicObjectSchema(getSchema(targetOutput.schemaId)) &&
            (!bothChangeSchemaOnConnection || targetHasSchemaSet)
          ) {
            addListSchemaIfNotExists(
              targetOutput.schemaId,
              sourceNode,
              (sourceSchemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions).dataProperties
            );
            changeSchemaFromConnection(targetOutput.schemaId, sourceNode, sourceSchemaChangeOnConnectionConfigProperty);
            if (sourceNodeDefinition?.inputsSchemaProperties) {
              const sourceSchemaDataProperties = (
                sourceSchemaChangeOnConnectionConfigProperty.options as ISchemaPickerOptions
              ).dataProperties;
              sourceNode.data.nodeProperties.inputs = buildNodeInputsFromSchema(
                sourceNode,
                targetOutput.schemaId,
                sourceNodeDefinition,
                reactFlowInstance,
                sourceSchemaDataProperties,
                targetOutput.schemaId
              );
              forceNodeUpdate(sourceNode, updateNodeInternals, reactFlowInstance);
            }
          }
        }
      }
    }

    if (
      (sourceHandleIsControlFlow && targetHandleIsControlFlow) ||
      (sourceHandleIsControlFlowError && targetHandleIsControlFlowError)
    ) {
      if (edgeIsValid) {
        newEdges.push({
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle,
          targetHandle: params.targetHandle,
          className:
            targetHandleIsControlFlowError && sourceHandleIsControlFlowError
              ? "control-flow-error-edge"
              : "control-flow-edge"
        } as Edge<any>);
      }
    } else if (edgeIsValid) {
      newEdges.push(params);
    }
  }

  const mappedNewEdges: Edge[] = [];
  newEdges.forEach((newEdge: Connection | Edge) => {
    const edgeId = getEdgeId(newEdge);
    if (!mappedNewEdges.some((e: Edge) => e.id === edgeId)) {
      mappedNewEdges.push({ ...newEdge, id: edgeId } as Edge);
    }
  });
  const returnedEdges: Edge[] = reactFlowInstance.getEdges().concat(mappedNewEdges);
  let newEdgesState: Edge[] = [];
  setEdges((edges: Edge[]) => {
    newEdgesState = edges;
    newEdges.forEach((newEdge: Connection | Edge) => (newEdgesState = addEdge(newEdge, newEdgesState)));
    return newEdgesState;
  });

  if (!loadingDocument) {
    // Update schemas linked to a schema source,
    //  but skip here if loading a document since this step needs to happen after initialize.
    updateLinkedSchemas(currentNodes, returnedEdges, updateNodeInternals, reactFlowInstance);
  }

  updateEdgeValidationStyling();
  nodesFromConnections.forEach((node) => {
    updateConnectedSchemaTags(node, currentNodes, returnedEdges);
  });

  return { validationResults: validationResults, newEdges: returnedEdges };
};

export const updateLinkedSchemas = (
  nodes: Node<any>[],
  edges: (Edge | Connection)[],
  updateNodeInternals: UpdateNodeInternals,
  reactFlowInstance: ReactFlowInstance
) => {
  const schemaSourceNodes = nodes.filter(
    (n: Node<any>) =>
      globalThis.nodeTypeDefinitions
        .getDefinition(n.type!)
        ?.nodeConfigProperties.some((p: INodeProperty) => p.schemaSourceSystemType)
  );
  schemaSourceNodes.forEach((schemaSourceNode: Node<any>) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(schemaSourceNode.type!)!;
    const schemaSourceProperty = nodeDefinition.nodeConfigProperties.find(
      (nodeProperty: INodeProperty) => nodeProperty.schemaSourceSystemType
    );
    const schemaSourceSystemType = schemaSourceProperty?.schemaSourceSystemType!;
    const oldSchemaLinkedNodes: Node<any>[] = schemaSourceNode.data.nodeProperties.schemaLinkedNodes;
    schemaSourceNode.data.nodeProperties.schemaLinkedNodes = [];
    Object.keys(schemaSourceNode.data.nodeProperties.outputs).forEach((outputName: string) => {
      if (!sourceSystemIncludesDataParam(schemaSourceSystemType, outputName)) {
        return;
      }

      const output = schemaSourceNode.data.nodeProperties.outputs[outputName];
      const outputSchema = getSchema(output.schemaId);
      if (
        outputSchema?.schemaType === DocumentSchemaType.object ||
        outputSchema?.schemaType === DocumentSchemaType.list
      ) {
        traverseSchemaLinkedOutputs({
          node: schemaSourceNode,
          nodes: nodes,
          edges: edges,
          outputsFilter: [outputName],
          onNodeVisit: (node) => {
            if (node.id !== schemaSourceNode.id) {
              node.data.nodeProperties.schemaSourceSystem = getSourceSystem(schemaSourceSystemType);
              node.data.nodeProperties.schemaSourceNodeId = schemaSourceNode.id;
              schemaSourceNode.data.nodeProperties.schemaLinkedNodes.push(node);
              node.data.nodeProperties.schemaLinkedInputs = [];
              node.data.nodeProperties.schemaLinkedOutputs = [];
            }
          },
          onInputVisit: (input, node, fromNode, outputName) => {
            if (node.id !== schemaSourceNode.id) {
              const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!)!;
              const output = fromNode.data.nodeProperties.outputs[outputName];
              let newInputSchemaId = getEmptySchemaId(input.type);
              if (output) {
                newInputSchemaId = output.schemaId;
              }
              if (!node.data.nodeProperties.schemaLinkedInputs.some((i: IDataParam) => i.name === input.name)) {
                node.data.nodeProperties.schemaLinkedInputs.push(input);
              }
              if (input.schemaId !== newInputSchemaId) {
                input.schemaId = newInputSchemaId;
                if (nodeDefinition.outputsSchemaProperties && !isDynamicObjectSchema(getSchema(newInputSchemaId))) {
                  node.data.nodeProperties.outputs = createDataParamsFromSchema(
                    node,
                    input.schemaId!,
                    false,
                    reactFlowInstance
                  );
                  forceNodeUpdate(node, updateNodeInternals, reactFlowInstance);
                }
              }
            }
          },
          onOutputVisit: (input, output, node) => {
            if (node.id !== schemaSourceNode.id) {
              const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!)!;
              if (
                !nodeDefinition.outputsSchemaProperties &&
                (isObjectLike(output.type) ||
                  output.type === DataType.OBJECTREFERENCE ||
                  output.type === DataType.OBJECTREFERENCELIST) &&
                !nodeDefinition.nodeConfigProperties.some(
                  (p: INodeProperty) =>
                    p.schemaSourceSystemType && sourceSystemIncludesDataParam(p.schemaSourceSystemType, output.name)
                )
              ) {
                let newOutputSchemaId = getEmptySchemaId(output.type);

                // Assign list item schema if output is an object and input is a list.
                if (!isListType(output.type) && isListType(input.type)) {
                  if (input.schemaId) {
                    const listSchema = getSchema(input.schemaId) as unknown as IDocumentListSchema;
                    if (listSchema.listItemSchema) {
                      const listItemSchema = getSchema(listSchema.listItemSchema);
                      if (listItemSchema) {
                        newOutputSchemaId = listSchema.listItemSchema;
                      }
                    }
                  }
                } else {
                  newOutputSchemaId = input.schemaId!;
                }
                output.schemaId = newOutputSchemaId;
                // Assign to output object on node, output param is a copy and not original object.
                node.data.nodeProperties.outputs[output.name].schemaId = newOutputSchemaId;
                node.data.nodeProperties.schemaLinkedOutputs.push(output);
              }
            }
          }
        });
      }
    });
    if (oldSchemaLinkedNodes) {
      const unlinkedNodes = oldSchemaLinkedNodes.filter(
        (n: Node<any>) =>
          !schemaSourceNode.data.nodeProperties.schemaLinkedNodes.some((ln: Node<any>) => ln.id === n.id)
      );
      unlinkedNodes.forEach((n: Node<any>) => {
        const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(n.type!)!;
        const isSchemaSource = nodeDefinition.nodeConfigProperties?.some(
          (nodeConfigProperty: INodeProperty) => nodeConfigProperty.schemaSourceSystemType !== undefined
        );

        if (!isSchemaSource) {
          delete n.data.nodeProperties.schemaSourceSystem;
          delete n.data.nodeProperties.schemaSourceNodeId;

          if (nodeDefinition.outputsSchemaProperties) {
            // Unlinked node outputs are empty object,
            //  therefore remove all outputs when they derive from object schema properties.
            n.data.nodeProperties.outputs = {};
            forceNodeUpdate(n, updateNodeInternals, reactFlowInstance);
          }

          n.data.nodeProperties.schemaLinkedInputs?.forEach(
            (input: IDataParam) => (input.schemaId = getEmptySchemaId(input.type))
          );
          n.data.nodeProperties.schemaLinkedOutputs?.forEach(
            (output: IDataParam) => (output.schemaId = getEmptySchemaId(output.type))
          );

          delete n.data.nodeProperties.schemaLinkedOutputs;
          delete n.data.nodeProperties.schemaLinkedInputs;
        }
      });
    }
  });
};

export const createDataParamsFromSchema = (
  node: Node<any>,
  schemaId: string,
  identifiersOnly: boolean,
  reactFlowInstance: ReactFlowInstance,
  forSchemaProperty?: string,
  excludeIdentifiers?: boolean,
  forceOptional?: boolean
) => {
  const objectSchema = getSchema(schemaId)! as unknown as IDocumentObjectSchema;
  const dataParams: any = {};
  Object.keys(objectSchema.properties).forEach((propertyName: string) => {
    if (!forSchemaProperty || forSchemaProperty === propertyName) {
      const edgeDetail = new EdgeDetail(node.id, reactFlowInstance);
      const schemaProperty = objectSchema.properties[propertyName]!;
      let schemaPropertyIsIdentifier = propertyIsIdentifier(schemaProperty);
      const schemaPropertyIsCompositeIdentifier = propertyIsCompositeIdentifier(schemaProperty);
      let expandedProperties = {};
      expandedProperties[propertyName] = schemaProperty;

      if (excludeIdentifiers !== true && identifiersOnly && schemaPropertyIsCompositeIdentifier) {
        const schemaPropertySchema = getSchema(schemaProperty.schema);
        if (schemaPropertySchema.schemaType === DocumentSchemaType.object) {
          const objectPropertySchema = schemaPropertySchema as unknown as IDocumentObjectSchema;
          schemaPropertyIsIdentifier = true;
          expandedProperties = objectPropertySchema.properties;
        }
      }

      Object.keys(expandedProperties).forEach((expandedPropertyName: string) => {
        const expandedSchemaProperty = expandedProperties[expandedPropertyName];
        const camelCaseSchema = upperSnakeToCamelCase(expandedSchemaProperty.schema);
        const primitiveSchema = dataTypes.find((dt: string) => dt === camelCaseSchema);
        const paramSchema = primitiveSchema ?? expandedSchemaProperty.schema;
        const paramRequired = forceOptional
          ? false
          : excludeIdentifiers !== true && identifiersOnly
          ? schemaPropertyIsIdentifier || schemaPropertyIsCompositeIdentifier
          : expandedSchemaProperty.required;
        const paramEnabled =
          !forceOptional &&
          ((excludeIdentifiers !== true && identifiersOnly && paramRequired) ||
            reactFlowInstance
              .getEdges()
              .some(
                (e: Edge) =>
                  e.sourceHandle === edgeDetail.getInput(expandedPropertyName)?.sourceHandle.handleId ||
                  e.targetHandle === edgeDetail.getOutput(expandedPropertyName)?.targetHandle.handleId
              ));
        if (
          (excludeIdentifiers !== true && (schemaPropertyIsIdentifier || !identifiersOnly)) ||
          (excludeIdentifiers === true && !schemaPropertyIsIdentifier)
        ) {
          dataParams[expandedPropertyName] = {
            name: expandedPropertyName,
            type: getDataTypeFromSchema(paramSchema),
            schemaId: paramSchema,
            required: paramRequired,
            enabled: paramEnabled || paramRequired,
            hideLabel: false,
            label: expandedSchemaProperty.label
          } as IDataParam;
        }
      });
    }
  });
  return dataParams;
};

export const buildNodeInputsFromSchema = (
  node: Node<any>,
  schemaId: string,
  nodeDefinition: INodeTypeDefinition,
  reactFlowInstance: ReactFlowInstance,
  schemaDataProperties?: string[],
  staticSchemaId?: string,
  forSchemaProperty?: string
): Record<string, IDataParam> => {
  const schemaInputs = createDataParamsFromSchema(
    node,
    schemaId,
    nodeDefinition.inputsSchemaProperties?.identifiersOnly ?? false,
    reactFlowInstance,
    forSchemaProperty,
    nodeDefinition.inputsSchemaProperties?.excludeIdentifiers,
    nodeDefinition.inputsSchemaProperties?.forceOptional
  );
  const staticInputs: Record<string, IDataParam> = {};
  nodeDefinition.dataInputs.forEach((i: IDataParam) => {
    if (schemaDataProperties?.includes(i.name)) {
      staticInputs[i.name] = { ...i, schemaId: staticSchemaId };
    } else {
      staticInputs[i.name] = i;
    }
  });
  Object.keys(staticInputs).forEach((key) => delete schemaInputs[key]);
  return { ...staticInputs, ...schemaInputs };
};

interface ITraverseSchemaLinkedOutputsRequest {
  node: Node<any>;
  nodes: Node<any>[];
  edges: (Edge | Connection)[];
  fromEdge?: Edge | Connection;
  onNodeVisit?: (node: Node<any>, traversedAlongsideSchema?: IDesignerSchema) => void;
  onInputVisit?: (
    input: IDataParam,
    node: Node<any>,
    fromNode: Node<any>,
    outputName: string,
    traversedAlongsideSchema?: IDesignerSchema
  ) => void;
  onOutputVisit?: (
    input: IDataParam,
    output: IDataParam,
    node: Node<any>,
    edges: (Edge | Connection)[],
    traversedAlongsideSchema?: IDesignerSchema
  ) => void;
  outputsFilter?: string[];
  traverseAlongsideSchema?: IDesignerSchema;
}

const traverseSchemaLinkedOutputs = (request: ITraverseSchemaLinkedOutputsRequest) => {
  const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(request.node.type!)!;

  const inputName = getNameFromDataHandleId(request.fromEdge?.sourceHandle ?? "");
  const input = request.node.data.nodeProperties.inputs[inputName];
  const inputIsFromSourceSystem = nodeDefinition.nodeConfigProperties.some(
    (nodeConfigProperty: INodeProperty) =>
      nodeConfigProperty.schemaSourceSystemType !== undefined &&
      inputName &&
      sourceSystemIncludesDataParam(nodeConfigProperty.schemaSourceSystemType!, inputName)
  );

  if (inputIsFromSourceSystem) {
    return;
  }

  if (request.onNodeVisit) {
    request.onNodeVisit(request.node, request.traverseAlongsideSchema);
  }

  if (request.fromEdge && request.onInputVisit && input) {
    const outputName = getNameFromDataHandleId(request.fromEdge.targetHandle!);
    const fromNode = request.nodes.find((n: Node<any>) => n.id === request.fromEdge?.target)!;
    request.onInputVisit(input, request.node, fromNode, outputName, request.traverseAlongsideSchema);
  }

  Object.keys(request.node.data.nodeProperties.outputs).forEach((outputName: string) => {
    if (request.outputsFilter && !request.outputsFilter.includes(outputName)) {
      return;
    }
    const output = request.node.data.nodeProperties.outputs[outputName];
    const outputEdges = request.edges.filter(
      (e: Edge) =>
        e.target === request.node.id && e.targetHandle === getOutputHandle(outputName, output.type).toHandleId()
    );
    let traversedSchema: IDesignerSchema | undefined = undefined;

    if (request.traverseAlongsideSchema) {
      traversedSchema = getSchema(input.schemaId);

      if (input?.type === "object" && nodeDefinition.outputsSchemaProperties) {
        const objectSchema = request.traverseAlongsideSchema as unknown as IDocumentObjectSchema;
        const propertySchemaId = objectSchema?.properties[outputName]?.schema;
        if (propertySchemaId) {
          traversedSchema = getSchema(propertySchemaId);
        }
      }

      if (isListType(input?.type) && (output?.type === "object" || output?.type === "objectReference")) {
        const objectListSchema = request.traverseAlongsideSchema as unknown as IDocumentListSchema;
        if (objectListSchema) {
          const objectSchemaId = objectListSchema.listItemSchema;
          if (objectSchemaId) {
            traversedSchema = getSchema(objectSchemaId);
          }
        }
      }
    }

    // Verify data types match between output and the traversed schema.
    if (traversedSchema && output.type !== getDataTypeFromSchemaInstance(traversedSchema)) {
      traversedSchema = undefined;
    }

    if (request.onOutputVisit) {
      request.onOutputVisit(input, { ...output, name: outputName }, request.node, outputEdges, traversedSchema);
    }

    if (
      isObjectLike(output.type) ||
      output.type === DataType.OBJECTREFERENCE ||
      output.type === DataType.OBJECTREFERENCELIST
    ) {
      outputEdges.forEach((e: Edge) => {
        if (isDynamicObjectSchema(getSchema(output.schemaId))) {
          return;
        }
        const nextNode = request.nodes.find((n: Node<any>) => n.id === e.source)!;
        const nextNodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(nextNode.type!)!;
        const nextNodeIsSchemaSource = nextNodeDefinition.nodeConfigProperties.some(
          (nodeConfigProperty: INodeProperty) =>
            nodeConfigProperty.schemaSourceSystemType !== undefined &&
            inputName &&
            sourceSystemIncludesDataParam(nodeConfigProperty.schemaSourceSystemType!, inputName)
        );
        if (!nextNodeIsSchemaSource) {
          let nonSchemaSourceOutputs: string[] = [];
          const nodeSchemaSourceProperty = nextNodeDefinition.nodeConfigProperties.find(
            (nodeConfigProperty: INodeProperty) => nodeConfigProperty.schemaSourceSystemType !== undefined
          );
          if (nodeSchemaSourceProperty) {
            nonSchemaSourceOutputs = Object.keys(nextNode.data.nodeProperties.outputs).filter(
              (outputName: string) =>
                !sourceSystemIncludesDataParam(nodeSchemaSourceProperty.schemaSourceSystemType!, outputName)
            );
          }

          traverseSchemaLinkedOutputs({
            node: nextNode,
            fromEdge: e,
            nodes: request.nodes,
            edges: request.edges,
            onNodeVisit: request.onNodeVisit,
            onInputVisit: request.onInputVisit,
            onOutputVisit: request.onOutputVisit,
            traverseAlongsideSchema: traversedSchema,
            outputsFilter: nonSchemaSourceOutputs.length > 0 ? nonSchemaSourceOutputs : undefined
          });
        }
      });
    }
  });
};

export const getDesignerDataEdge = (
  input: IDataParam,
  outputType: DataType,
  sourceNodeId: string,
  outputName: string,
  targetNodeId: string
) => {
  const connection = {
    source: sourceNodeId,
    sourceHandle: getInputHandle(input.name, input.type).toHandleId(),
    target: targetNodeId,
    targetHandle: getOutputHandle(outputName, outputType).toHandleId()
  };
  return {
    ...connection,
    id: getEdgeId(connection)
  };
};

export class DataHandleId {
  constructor(
    public readonly name: string,
    public readonly type: string,
    public readonly direction: "input" | "output"
  ) {}
  toHandleId(): string {
    return `${this.name}-${this.type}-${this.direction}`;
  }
  isInput(): boolean {
    return this.direction === "input";
  }
  isOutput(): boolean {
    return this.direction === "output";
  }
}

export const parseDataHandleId = (handleId: string): DataHandleId => {
  if (!handleId) return new DataHandleId("", "", "input");
  const parts = handleId.split("-");
  if (parts.length < 3) {
    // Not a standard data handle (e.g. a control-flow handle like "FlowOutHandleDone").
    // Return the full handle string as the name so callers that match on `.name` still work.
    return new DataHandleId(handleId, "", "input");
  }
  const direction = parts.at(-1) as "input" | "output";
  const type = parts.at(-2) ?? "";
  const name = parts.slice(0, -2).join("-");
  return new DataHandleId(name, type, direction);
};

export const getEdgeId = (edge: Edge | Connection) => {
  return "reactflow__edge-" + edge.source + edge.sourceHandle + "-" + edge.target + edge.targetHandle;
};

export const getInputHandle = (name: string, type: string): DataHandleId => new DataHandleId(name, type, "input");

export const getOutputHandle = (name: string, type: string): DataHandleId => new DataHandleId(name, type, "output");

export const getDesignerControlEdge = (
  inputName: string,
  sourceNodeId: string,
  outputName: string,
  targetNodeId: string
) => {
  const connection = {
    source: sourceNodeId,
    sourceHandle: "FlowInHandle" + inputName,
    target: targetNodeId,
    targetHandle: `FlowOutHandle${outputName}`
  };
  return {
    ...connection,
    id: getEdgeId(connection),
    className: "control-flow-edge"
  };
};

export const getNameFromControlHandleId = (handleId: string) =>
  handleId.replace("FlowInHandle", "").replace("FlowOutHandle", "");

export const getNameFromDataHandleId = (handleId: string): string => parseDataHandleId(handleId).name;

export const getDataTypeFromDataHandleId = (handleId: string): string => parseDataHandleId(handleId).type;

export const dataHandleIsInput = (handleId: string): boolean => parseDataHandleId(handleId).isInput();

export const dataHandleIsOutput = (handleId: string): boolean => parseDataHandleId(handleId).isOutput();

export const resetReferenceSchemas = (
  deletingEdges: Edge[],
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals
) => {
  const connectedNodes = reactFlowInstance
    .getNodes()
    .filter((n: Node<any>) => deletingEdges.some((e: Edge) => e.source === n.id || e.target === n.id));
  connectedNodes.forEach((n: Node<any>) => {
    // Check for nullable reference handles, and reset to no reference
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(n.type!);
    const deletingInputNames = deletingEdges.map((e: Edge) => getNameFromDataHandleId(e.sourceHandle!));
    nodeDefinition?.dataInputs.forEach((input: IDataParam) => {
      if (
        nodeDefinition.adaptToReferenceSchemaOnConnection?.resetOnDisconnect &&
        input.type === DataType.OBJECTREFERENCE &&
        input.schemaId === noReferenceSchemaId &&
        deletingInputNames.includes(input.name)
      ) {
        n.data.nodeProperties.inputs[input.name].schemaId = noReferenceSchemaId;
      }
    });
    const newEdges = reactFlowInstance
      .getEdges()
      .filter((e: Edge) => !deletingEdges.some((de: Edge) => de.id === e.id));
    const deletingOutputNames = deletingEdges.map((e: Edge) => getNameFromDataHandleId(e.targetHandle!));
    nodeDefinition?.dataOutputs.forEach((output: IDataParam) => {
      const outputIsUnconnected = !newEdges.some((e: Edge) => getNameFromDataHandleId(e.targetHandle!) === output.name);
      if (
        nodeDefinition.adaptToReferenceSchemaOnConnection?.resetOnDisconnect &&
        output.type === DataType.OBJECTREFERENCE &&
        output.schemaId === noReferenceSchemaId &&
        deletingOutputNames.includes(output.name) &&
        outputIsUnconnected
      ) {
        n.data.nodeProperties.outputs[output.name].schemaId = noReferenceSchemaId;
      }
    });
    forceNodeUpdate(n, updateNodeInternals, reactFlowInstance);
  });
};

export interface IEdgeDeletionMode {
  inputEdges: boolean;
  outputEdges: boolean;
  controlEdges: boolean;
  filteredProperties?: string[];
  excludeEdge?: (edge: Edge) => boolean;
}

export const allEdgesDeletionMode = { inputEdges: true, outputEdges: true, controlEdges: true };

const deleteEdges = (
  deletingEdges: Edge[],
  nodeIds: string[],
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals,
  currentEdges?: Edge[]
) => {
  const connectedNodes = reactFlowInstance
    .getNodes()
    .filter(
      (n: Node<any>) =>
        !nodeIds.some((nodeId: string) => nodeId === n.id) &&
        deletingEdges.some((e) => e.source === n.id || e.target === n.id)
    );
  let newEdges: Edge[] = [];
  reactFlowInstance.setEdges((edges) => {
    newEdges = (currentEdges ?? edges).filter((e) => !deletingEdges.some((de: Edge<any>) => e.id === de.id));
    return newEdges;
  });

  resetReferenceSchemas(deletingEdges, reactFlowInstance, updateNodeInternals);

  // Need to remove the node from anchored nodes list on the parent
  reactFlowInstance.setNodes((nds) => {
    nds.forEach((updatedNode: Node<any>) => {
      const anchoredDeletingNode = connectedNodes.find(
        (connectedNode: Node<any>) =>
          connectedNode.data.nodeProperties.anchoredToNodeId === updatedNode.id &&
          deletingEdges.some(
            (e: Edge) =>
              (e.source === updatedNode.id && e.target === connectedNode.id) ||
              (e.target === updatedNode.id && e.source === connectedNode.id)
          )
      );
      if (anchoredDeletingNode) {
        delete anchoredDeletingNode.data.nodeProperties.anchoredToNodeId;
        updatedNode.data.nodeProperties.anchoredNodes = updatedNode.data.nodeProperties.anchoredNodes.filter(
          (anchor: AnchorProperties) => anchor.nodeId !== anchoredDeletingNode.id
        );
      }

      if (connectedNodes.some((c: Node<any>) => c.id === updatedNode.id)) {
        // This is an undoing of what docking does. The zIndex is modified as part of docking,
        // since the nodes need to sit on top of each other. Removing the zIndex resets this.
        updatedNode.zIndex = undefined;
      }
      updateNodeTimestamp(updatedNode);
    });
    return nds;
  });

  updateEdgeValidationStyling(newEdges);

  return newEdges;
};

export const deleteEdgesFromNodes = (
  nodeIds: string[],
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals,
  mode: IEdgeDeletionMode = allEdgesDeletionMode,
  currentEdges?: Edge[]
): Edge[] => {
  const deletingEdges = getDeletingEdgesFromNodes(nodeIds, reactFlowInstance, mode, currentEdges);
  return deleteEdges(deletingEdges, nodeIds, reactFlowInstance, updateNodeInternals, currentEdges);
};

export const getDeletingEdgesFromNodes = (
  nodeIds: string[],
  reactFlowInstance: ReactFlowInstance,
  mode: IEdgeDeletionMode = allEdgesDeletionMode,
  currentEdges?: Edge[]
): Edge[] => {
  let deletingEdges = (currentEdges ?? reactFlowInstance.getEdges()).filter((e) =>
    nodeIds.some(
      (nodeId: string) =>
        ((mode.inputEdges && e.source === nodeId) || (mode.outputEdges && e.target === nodeId)) &&
        (mode.controlEdges ||
          (!mode.controlEdges && e.sourceHandle?.indexOf("FlowIn") !== 0 && e.targetHandle?.indexOf("FlowOut") !== 0))
    )
  );
  if (mode.filteredProperties) {
    deletingEdges = deletingEdges.filter((e: Edge<any>) =>
      mode.filteredProperties!.some(
        (propertyName: string) => e.sourceHandle?.includes(propertyName) || e.targetHandle?.includes(propertyName)
      )
    );
  }
  if (mode.excludeEdge) {
    deletingEdges = deletingEdges.filter((e: Edge) => !mode.excludeEdge!(e));
  }

  return deletingEdges;
};

export const deleteEdgesFromSchemaProperty = (
  schemaId: string,
  propertyName: string,
  propertyDataType: DataType,
  reactFlowInstance: ReactFlowInstance,
  updateNodeInternals: UpdateNodeInternals
) => {
  let deletingEdges: Edge[] = [];
  const nodeIds: string[] = [];
  reactFlowInstance.getNodes().forEach((node: Node<any>) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
    let edgesDeletedFromNode = false;
    if (nodeDefinition?.inputsSchemaProperties) {
      const outputHasSchema = Object.keys(node.data.nodeProperties.outputs).some(
        (outputName: string) => node.data.nodeProperties.outputs[outputName].schemaId === schemaId
      );
      if (outputHasSchema) {
        const edgesToDelete = reactFlowInstance
          .getEdges()
          .filter(
            (e: Edge<any>) =>
              e.sourceHandle === getInputHandle(propertyName, propertyDataType).toHandleId() && e.source === node.id
          );
        deletingEdges = deletingEdges.concat(edgesToDelete);
        edgesDeletedFromNode = true;
      }
    }

    if (nodeDefinition?.outputsSchemaProperties) {
      const inputWithSchema = Object.keys(node.data.nodeProperties.inputs).some(
        (inputName: string) => node.data.nodeProperties.inputs[inputName].schemaId === schemaId
      );
      if (inputWithSchema) {
        const edgesToDelete = reactFlowInstance
          .getEdges()
          .filter(
            (e: Edge<any>) =>
              e.targetHandle === getOutputHandle(propertyName, propertyDataType).toHandleId() && e.target === node.id
          );
        deletingEdges = deletingEdges.concat(edgesToDelete);
        edgesDeletedFromNode = true;
      }
    }

    if (edgesDeletedFromNode) {
      nodeIds.push(node.id);
    }
  });

  deleteEdges(deletingEdges, nodeIds, reactFlowInstance, updateNodeInternals);
};
